import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import Keycloak from 'keycloak-js';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { LoadPermissionsSuccess } from 'src/app/store-ngxs/actions/permissions.actions';
import { ConfigService } from '../core/services/config-service';
import { Token } from '../models/token.model';

interface RolePermissionsResponse {
  permissions: string[];
}

// Keycloak client whose resource_access roles count as app roles (other clients are ignored)
const ORIGIN_ROLES_CLIENT_ID = 'Origin';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private accessTokenKey = 'accessToken';
  private refreshTokenKey = 'refreshToken';
  public token$!: Observable<Token>;
  private kc: Keycloak;
  private permissions: string[] = [];

  constructor(
    private config: ConfigService,
    private http: HttpClient,
    private ngxsStore: Store
  ) { }

  async init(): Promise<boolean> {
    const cfg = this.config.readConfig();
    this.kc = new Keycloak({
      url: cfg.authUrl,
      realm: cfg.authRealm,
      clientId: cfg.authClientId,
    });

    this.kc.onAuthSuccess = () => this.persistTokens();
    this.kc.onAuthRefreshSuccess = () => this.persistTokens();
    this.kc.onAuthError = (err) => this.authError(err);
    this.kc.onAuthRefreshError = () => this.clearStoredTokens();
    this.kc.onAuthLogout = () => this.clearStoredTokens();

    const token = localStorage.getItem(this.accessTokenKey) ?? undefined;
    const refreshToken = localStorage.getItem(this.refreshTokenKey) ?? undefined;

    let authenticated = false;
    try {
      authenticated = await this.kc.init({ pkceMethod: 'S256', token, refreshToken, checkLoginIframe: false });
    } catch (err) {
      console.error('[AuthService] kc.init failed:', err);
      authenticated = false;
    }

    console.log('[AuthService] kc.init result: authenticated =', authenticated);

    if (!authenticated) {
      this.clearStoredTokens();
      await this.kc.login();
      // kc.login() only starts the redirect; it resolves before the browser
      // actually navigates away. Never resolve from here so the app never
      // finishes bootstrapping (and fires requests) during that window.
      return new Promise<boolean>(() => { });
    } else if (!this.isAdmin()) {
      await firstValueFrom(this.fetchAndCachePermissions());
    }
    return authenticated;
  }

  private authError(err: any): void {
    console.error('[AuthService] kc error authenticating:', err);
  }

  private persistTokens(): void {
    if (this.kc.token) {
      localStorage.setItem(this.accessTokenKey, this.kc.token);
    }
    if (this.kc.refreshToken) {
      localStorage.setItem(this.refreshTokenKey, this.kc.refreshToken);
    }
  }

  private clearStoredTokens(err?: any): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  isLoggedIn(): boolean {
    return !!this.kc.authenticated;
  }

  public isAuthenticated(): boolean {
    return this.kc.authenticated;
  }

  login(redirectPath?: string): void {
    if (redirectPath) {
      const redirectUri = window.location.origin + redirectPath;
      this.kc.login({ redirectUri: redirectUri });
    }
    else {
      this.kc.login();
    }
  }

  logout(): void {
    this.clearStoredTokens();
    this.kc.logout({ redirectUri: window.location.origin });
  }

  getToken(): string {
    return this.kc.token || '';
  }

  async getValidToken(): Promise<string | undefined> {
    if (!this.kc) {
      return '';
    }
    if (!this.kc.refreshToken) {
      return this.kc.token;
    }
    try {
      await this.kc.updateToken(30);
    } catch (err) {
      this.kc.login();
    }
    return this.kc.token;
  }

  public getUserRole(): string {
    const roles = this.getRoles();
    // Keycloak always includes these on every user; skip them to find the actual assigned role.
    const role = roles.find(
      (r: string) => r !== 'offline_access' && r !== 'uma_authorization' && !r.startsWith('default-roles-')
    );
    return role ?? '';
  }

  public getUserId(): string {
    return this.kc.tokenParsed?.sub ?? '';
  }

  public isAdmin(): boolean {
    return this.getRoles().includes('admin');
  }

  private getRoles(): string[] {
    const token = this.kc.tokenParsed;
    if (!token) {
      return [];
    }

    const roles = token['roles'];
    if (Array.isArray(roles)) {
      return roles;
    }

    // No flat "roles" claim: fall back to Keycloak's nested claims. Take all realm roles,
    // but client roles only from the Origin client (matched case-insensitively).
    const realmRoles = token.realm_access?.roles ?? [];
    const originClient = Object.entries(token.resource_access ?? {}).find(
      ([clientId]) => clientId.toLowerCase() === ORIGIN_ROLES_CLIENT_ID.toLowerCase()
    );
    const clientRoles = originClient?.[1]?.roles ?? [];

    return [...new Set([...realmRoles, ...clientRoles])];
  }

  fetchAndCachePermissions(): Observable<string[]> {
    const url = `${this.config.readConfig().baseUrlGateway}/useraccount/permissions`;
    return this.http.get<RolePermissionsResponse>(url).pipe(
      map((response) => {
        this.permissions = [...new Set(response.permissions.map((p) => p.toLocaleLowerCase()))];
        this.ngxsStore.dispatch(new LoadPermissionsSuccess(response.permissions));
        return this.permissions;
      }),
      catchError((error) => {
        console.error('Error fetching permissions:', error);
        return of([]);
      })
    );
  }

  hasPermission(permissionValue: string | string[]): boolean {
    if (this.isAdmin()) {
      return true;
    }
    const values = Array.isArray(permissionValue) ? permissionValue : [permissionValue];
    return values.every((value) => {
      const resource = value.split('.')[0].toLocaleLowerCase();
      return this.permissions.includes(resource + '.' + 'all') ||
        this.permissions.some((permission) =>
          permission.includes(value.toLocaleLowerCase())
        );
    });

  }
}
